import { InputHTMLAttributes, FC } from 'react';
import { Controller } from 'react-hook-form';

import { FormBaseLabel } from './FormBaseLabel';
import { FormBaseError } from './FormBaseError';

import { StyledInput } from './StyleGuide';
import { defaultBorder } from '../../styles/style-guide';

import { RootState } from '../../store';
import { useAppSelector } from '../../hooks/hooksRedux';

/**
 * Props interface for the FormInput component.
 * It extends the attributes available for an HTML input element,
 * ensuring that properties like `id`, `name`, and `label` are always provided.
 */
export interface Props extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  name: string; // Override input's default attribute to make it mandatory
  label: string; // Label text for the input field
  control: any; // Control object from the useForm hook of react-hook-form
  error?: string | null; // Optional error message
  tooltip?: string; // Optional tooltip text
}

/**
 * FormInput component: A comprehensive input component integrating a label,
 * an input field, and an optional error message. It utilizes the react-hook-form's
 * Controller for integration with form management and validation.
 */
const FormInput: FC<Props> = ({ id, name, label, control, error, tooltip, ...rest }) => {
  const borderColors = useAppSelector((state: RootState) => state.styleGuide.data.colors);
  const primaryBorderColor = borderColors.primary || defaultBorder.primary;

  const colorError = '#ef4444';

  return (
    <div className="flex w-full flex-col gap-1">
      {/* Render the label with optional tooltip */}
      <FormBaseLabel id={id} label={label} tooltip={tooltip} error={error} />
      {/* Controller from react-hook-form to manage the input field */}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <StyledInput
            id={id}
            $error={error}
            $borderColor={primaryBorderColor}
            $errorColor={colorError}
            {...field}
            {...rest}
          />
        )}
      />
      {/* Conditionally render the error message if it exists */}
      {error && <FormBaseError error={error} />}
    </div>
  );
};

export { FormInput };
